import { Injectable } from '@angular/core';

export interface ErrorMessageMapping {
  title: string;
  message: string;
  userActionHint?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ErrorMessageService {
  private readonly errorMessages: Record<string, ErrorMessageMapping> = {
    'bad.request': {
      title: 'Ungültige Anfrage',
      message: 'Die Anfrage konnte nicht verarbeitet werden.',
      userActionHint:
        'Bitte überprüfen Sie Ihre Eingaben und versuchen Sie es erneut.',
    },
    unauthorized: {
      title: 'Nicht autorisiert',
      message: 'Sie sind nicht berechtigt, diese Aktion durchzuführen.',
      userActionHint:
        'Bitte melden Sie sich an oder wenden Sie sich an Ihren Administrator.',
    },
    forbidden: {
      title: 'Zugriff verweigert',
      message: 'Sie haben keine Berechtigung für diese Ressource.',
      userActionHint:
        'Wenden Sie sich an Ihren Administrator, um die notwendigen Berechtigungen zu erhalten.',
    },
    'not.found': {
      title: 'Nicht gefunden',
      message: 'Die angeforderte Ressource wurde nicht gefunden.',
      userActionHint: 'Überprüfen Sie, ob die Ressource noch vorhanden ist.',
    },
    'server.error': {
      title: 'Serverfehler',
      message: 'Ein Serverfehler ist aufgetreten.',
      userActionHint:
        'Bitte versuchen Sie es später erneut oder wenden Sie sich an den Support.',
    },
    internal: {
      title: 'Interner Fehler',
      message: 'Ein interner Serverfehler ist aufgetreten.',
      userActionHint:
        'Bitte versuchen Sie es später erneut oder wenden Sie sich an den Support.',
    },
    'network.error': {
      title: 'Verbindungsfehler',
      message: 'Keine Verbindung zum Server möglich.',
      userActionHint:
        'Überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.',
    },

    'salvage.request.cancel.bids.existing': {
      title: 'Stornierung nicht möglich',
      message:
        'Die Restwertanfrage kann nicht storniert werden, da bereits Gebote vorhanden sind.',
      userActionHint:
        'Kontaktieren Sie den Anbieter direkt, um die Verwertung zu stornieren.',
    },
    'salvage.request.not.found': {
      title: 'Stornierung nicht möglich',
      message: 'Es wurde keine Restwertanfrage zu diesem Vorgang gefunden.',
    },
    'salvage.request.cancel': {
      title: 'Stornierung fehlgeschlagen',
      message: 'Die Restwertanfrage konnte nicht storniert werden.',
      userActionHint:
        'Versuchen Sie es erneut oder wenden Sie sich an den Support.',
    },
    'salvage.request.cancel.credentials.not.found': {
      title: 'Anmeldedaten fehlen',
      message:
        'Die Anmeldedaten für den Verwertungsanbieter wurden nicht gefunden.',
      userActionHint:
        'Wenden Sie sich an Ihren Administrator, um die Anmeldedaten zu konfigurieren.',
    },
    'salvage.request.publish.exchange.error': {
      title: 'Einstellen nicht erfolgreich',
      message:
        'Ein unbekannter Fehler ist beim Einstellen in die Restwertbörse aufgetreten.',
      userActionHint:
        'Versuchen Sie es erneut oder wenden Sie sich an den Support.',
    },
    'salvage.request.bids.error': {
      title: 'Gebotsabruf fehlgeschlagen',
      message: 'Die Gebote konnten nicht abgerufen werden.',
      userActionHint:
        'Versuchen Sie es erneut oder wenden Sie sich an den Support.',
    },

    'missing.vehicle.data': {
      title: 'Fahrzeugdaten unvollständig',
      message: 'Erforderliche Fahrzeugdaten fehlen für diese Aktion.',
      userActionHint:
        'Ergänzen Sie die fehlenden Fahrzeugdaten und versuchen Sie es erneut.',
    },
    'no.insurance.company.number': {
      title: 'Versicherungsunternehmen fehlt',
      message: 'Die Nummer des Versicherungsunternehmens ist nicht hinterlegt.',
      userActionHint:
        'Wenden Sie sich an Ihren Administrator, um die Konfiguration zu vervollständigen.',
    },
    'no.case.type': {
      title: 'Schadenstyp fehlt',
      message: 'Der Schadenstyp ist nicht definiert.',
      userActionHint:
        'Setzen Sie einen gültigen Schadenstyp und versuchen Sie es erneut.',
    },

    'missing.credentials': {
      title: 'Anmeldedaten fehlen',
      message: 'Die erforderlichen Anmeldedaten wurden nicht gefunden.',
      userActionHint:
        'Konfigurieren Sie die Anmeldedaten in den Einstellungen.',
    },
    'claims.history.engine.invalid.credentials': {
      title: 'Ungültige Anmeldedaten',
      message: 'Die Anmeldedaten für die Schadenhistorie sind ungültig.',
      userActionHint: 'Überprüfen Sie die Anmeldedaten in den Einstellungen.',
    },

    'too.many.requests': {
      title: 'Zu viele Anfragen',
      message: 'Sie haben zu viele Anfragen in kurzer Zeit gesendet.',
      userActionHint:
        'Bitte warten Sie einen Moment und versuchen Sie es erneut.',
    },
    'unknown.error': {
      title: 'Unbekannter Fehler',
      message: 'Ein unbekannter Fehler ist aufgetreten.',
      userActionHint:
        'Bitte versuchen Sie es erneut oder wenden Sie sich an den Support.',
    },
    'calculation.dat.no.calculation': {
      title: 'Kalkulation abgebrochen',
      message: 'Die Kalkulation wurde in SilverDAT 3 abgebrochen.',
      userActionHint: 'Bitte erstellen Sie eine Kalkulation.',
    },
    'copart.pricefinder.invalid.credentials': {
      title: 'Ungültige Anmeldedaten',
      message: 'Die Anmeldedaten für den Copart Preisfinder sind ungültig.',
      userActionHint: 'Überprüfen Sie die Anmeldedaten in den Einstellungen.',
    },
    'salvage.request.publish.existing': {
      title: 'Anfrage wurde bereits erstellt',
      message:
        'Es gibt bereits eine aktive Restwertbörsen-Anfrage (Abholzeitpunkt wurde noch nicht erreicht).',
      userActionHint:
        'Bitte warten Sie, bis der Abholzeitpunkt erreicht ist, bevor Sie erneut eine Anfrage erstellen.',
    },
    'document.generation.failed': {
      title: 'Dokumentenerstellung fehlgeschlagen',
      message: 'Das Dokument konnte nicht erstellt werden.',
      userActionHint:
        'Bitte versuchen Sie es erneut oder wenden Sie sich an den Support.',
    },
    'document.scanner.file.wrong.format': {
      title: 'Ungültiges Dateiformat',
      message: 'Nur Bilder und PDF-Dateien sind erlaubt.',
      userActionHint:
        'Bitte versuchen Sie es erneut oder wenden Sie sich an den Support.',
    },
    'document.scanner.file.empty': {
      title: 'Die hochgeladene Datei ist leer',
      message: 'Die Datei ist leer und kann nicht verarbeitet werden.',
      userActionHint:
        'Bitte versuchen Sie es erneut oder wenden Sie sich an den Support.',
    },
    'document.scanner.file.too.big': {
      title: 'Die hochgeladene Datei ist zu groß',
      message: 'Die Datei darf maximal 20 MB groß sein.',
      userActionHint:
        'Bitte versuchen Sie es erneut oder wenden Sie sich an den Support.',
    },
    'invalid.file.type.pdf': {
      title: 'Ungültiges Dateiformat',
      message: 'Es können nur PDF-Dateien hochgeladen werden.',
      userActionHint:
        'Bitte wählen Sie eine Datei im PDF-Format und versuchen Sie es erneut.',
    },
    'invalid.file.type.image': {
      title: 'Ungültiges Dateiformat',
      message: 'Es können nur Bilddateien hochgeladen werden.',
      userActionHint:
        'Bitte wählen Sie eine Bilddatei (z.B. JPG, PNG) und versuchen Sie es erneut.',
    },
    'invalid.file.type': {
      title: 'Ungültiges Dateiformat',
      message: 'Der Dateityp wird nicht unterstützt.',
      userActionHint:
        'Bitte wählen Sie eine gültige Bild- oder PDF-Datei und versuchen Sie es erneut.',
    },
    'contactytpe.insurance.with.wrong.insurancecompanynumber': {
      title: 'Der Kontaktyp Versicherung kann nicht hinzugefügt werden',
      message: 'Die VU-Nummer stimmt nicht mit der vom Vorgang überein.',
      userActionHint:
        'Bitte überprüfen Sie die VU-Nummern und versuchen Sie es erneut oder wenden Sie sich an den Support.',
    },
    'document.scanner.failed': {
      title: 'Fahrzeugschenscanner fehlgeschlagen',
      message: 'Farzeugschein konnte nicht gescannt werden.',
      userActionHint:
        'Bitte versuchen Sie es erneut oder wenden Sie sich an den Support.',
    },
    'email.recipient.limit.exceeded': {
      title: 'E-Mail-Limit überschritten',
      message: 'Die maximale Anzahl an E-Mail-Empfängern wurde überschritten.',
      userActionHint:
        'Bitte reduzieren Sie die Anzahl der Empfänger und versuchen Sie es erneut.',
    },
  };

  getErrorMessage(key: string): ErrorMessageMapping {
    if (this.errorMessages[key]) {
      return this.errorMessages[key];
    }

    const mappedKey = this.mapErrorKey(key);
    if (mappedKey && this.errorMessages[mappedKey]) {
      return this.errorMessages[mappedKey];
    }

    return {
      title: 'Unbekannter Fehler',
      message: `Ein Fehler ist aufgetreten (${key}).`,
      userActionHint:
        'Bitte versuchen Sie es erneut oder wenden Sie sich an den Support.',
    };
  }

  hasErrorMapping(key: string): boolean {
    return (
      Object.prototype.hasOwnProperty.call(this.errorMessages, key) ||
      !!this.mapErrorKey(key)
    );
  }

  getAvailableKeys(): string[] {
    return Object.keys(this.errorMessages);
  }

  addErrorMapping(key: string, mapping: ErrorMessageMapping): void {
    this.errorMessages[key] = mapping;
  }

  private mapErrorKey(key: string): string | null {
    if (key === 'internal') {
      return 'server.error';
    }

    if (key.includes('vehicle') && key.includes('data')) {
      return 'missing.vehicle.data';
    }

    if (key.includes('credential') && key.includes('invalid')) {
      return 'missing.credentials';
    }

    if (key === '400' || key.startsWith('400.')) {
      return 'bad.request';
    }
    if (key === '401' || key.startsWith('401.')) {
      return 'unauthorized';
    }
    if (key === '403' || key.startsWith('403.')) {
      return 'forbidden';
    }
    if (key === '404' || key.startsWith('404.')) {
      return 'not.found';
    }
    if (key === '500' || key.startsWith('500.')) {
      return 'server.error';
    }

    return null;
  }
}
